import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import {
  BookOpen,
  Sparkles,
  Compass,
  Search,
  Coffee,
  FileText,
  PawPrint,
  Music,
  Image,
  Save,
  Trash2,
  Edit2,
  ChevronLeft,
  Download,
  Wand2,
  Plus,
  X,
  Printer,
  Eye,
  Heart,
  Rainbow,
  Mail,
  Lightbulb
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('pawsToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Story templates
const TEMPLATES = [
  { 
    id: "through-their-eyes", 
    name: "Through Their Eyes", 
    icon: Eye, 
    color: "from-sky-400 to-blue-500",
    description: "The world from your pet's perspective",
    prompts: ["their_view", "favorite_moment", "dreams", "treasure", "wish_you_knew"],
    isSpecial: true
  },
  { 
    id: "letter-to-pet", 
    name: "Letter to My Pet", 
    icon: Mail, 
    color: "from-pink-400 to-rose-500",
    description: "A heartfelt letter to your beloved companion",
    prompts: ["letter_opening", "letter_gratitude", "letter_memory", "letter_promise", "letter_closing"],
    isSpecial: true
  },
  { 
    id: "rainbow-bridge", 
    name: "Rainbow Bridge", 
    icon: Rainbow, 
    color: "from-violet-400 via-pink-400 to-amber-400",
    description: "A loving tribute to a pet who has crossed over",
    prompts: ["rainbow_intro", "rainbow_joy", "rainbow_lesson", "rainbow_missing", "rainbow_love"],
    isSpecial: true,
    isMemorial: true
  },
  { 
    id: "adventure", 
    name: "Tiny Adventure", 
    icon: Compass, 
    color: "from-amber-500 to-orange-500",
    description: "A small journey or discovery",
    prompts: ["Opening hook", "The goal", "First obstacle", "Second obstacle", "Big moment", "Ending"]
  },
  { 
    id: "mystery", 
    name: "Playful Mystery", 
    icon: Search, 
    color: "from-purple-500 to-indigo-500",
    description: "Something curious happened...",
    prompts: ["The clue", "Funny suspect", "Red herring", "The reveal", "Solution"]
  },
  { 
    id: "cozy", 
    name: "Cozy Day", 
    icon: Coffee, 
    color: "from-rose-400 to-pink-500",
    description: "A peaceful, heartwarming moment",
    prompts: ["The setting", "A comfort", "Small surprise", "Warm reflection"]
  },
  { 
    id: "blank", 
    name: "Blank Canvas", 
    icon: FileText, 
    color: "from-sage to-teal-500",
    description: "Complete creative freedom",
    prompts: []
  }
];

// Genre options
const GENRES = [
  { id: "adventure", label: "Adventure", emoji: "🗺️" },
  { id: "mystery", label: "Mystery", emoji: "🔍" },
  { id: "comedy", label: "Comedy", emoji: "😄" },
  { id: "slice-of-life", label: "Slice of Life", emoji: "☀️" },
  { id: "fantasy", label: "Fantasy", emoji: "✨" }
];

// Mood options
const MOODS = [
  { id: "playful", label: "Playful", emoji: "🎈" },
  { id: "brave", label: "Brave", emoji: "🦁" },
  { id: "curious", label: "Curious", emoji: "🔮" },
  { id: "silly", label: "Silly", emoji: "🤪" },
  { id: "cozy", label: "Cozy", emoji: "🧸" }
];

export const StorybookCreatorPage = ({ pets = [], songs = [] }) => {
  const [entries, setEntries] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingStarter, setGeneratingStarter] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    genre: "",
    mood: "",
    pet_id: "",
    pet_name: "",
    pet_traits: "",
    opening: "",
    goal: "",
    obstacles: ["", ""],
    climax: "",
    ending: "",
    full_story: "",
    illustration: "",
    paired_song_id: "",
    memory_note: "",
    // Through Their Eyes fields
    their_view: "",
    favorite_moment: "",
    dreams: "",
    treasure: "",
    wish_you_knew: "",
    // Letter to My Pet fields
    letter_opening: "",
    letter_gratitude: "",
    letter_memory: "",
    letter_promise: "",
    letter_closing: "",
    // Rainbow Bridge fields
    rainbow_intro: "",
    rainbow_joy: "",
    rainbow_lesson: "",
    rainbow_missing: "",
    rainbow_love: ""
  });
  
  // Story Seeds - fetch random memories for inspiration
  const [storySeeds, setStorySeeds] = useState([]);
  const [loadingSeeds, setLoadingSeeds] = useState(false);

  // Fetch entries
  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/storybook`, { headers: getAuthHeader() });
      setEntries(response.data);
    } catch (e) {
      console.error("Failed to fetch storybook entries:", e);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Fetch Story Seeds (random memories/journal entries for inspiration)
  const fetchStorySeeds = async () => {
    setLoadingSeeds(true);
    try {
      // Fetch moments, gratitude entries, and stories for inspiration
      const [momentsRes, gratitudeRes, storiesRes] = await Promise.all([
        axios.get(`${API}/moments`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/gratitude`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/stories`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      const seeds = [];
      
      // Add moments as seeds
      momentsRes.data?.slice(0, 5).forEach(m => {
        if (m.caption || m.description) {
          seeds.push({
            type: "moment",
            text: m.caption || m.description,
            emoji: "📸",
            pet_name: m.pet_name
          });
        }
      });
      
      // Add gratitude entries as seeds
      gratitudeRes.data?.slice(0, 5).forEach(g => {
        if (g.content) {
          seeds.push({
            type: "gratitude",
            text: g.content.slice(0, 100) + (g.content.length > 100 ? "..." : ""),
            emoji: "🙏",
            pet_name: g.pet_name
          });
        }
      });
      
      // Add stories as seeds
      storiesRes.data?.slice(0, 5).forEach(s => {
        if (s.content) {
          seeds.push({
            type: "story",
            text: s.content.slice(0, 100) + (s.content.length > 100 ? "..." : ""),
            emoji: "📖",
            pet_name: s.pet_name
          });
        }
      });
      
      // Shuffle and take random 3
      const shuffled = seeds.sort(() => Math.random() - 0.5).slice(0, 3);
      setStorySeeds(shuffled);
      
      if (shuffled.length === 0) {
        toast.info("No memories found yet. Add moments or journal entries to get story inspiration!");
      }
    } catch (e) {
      console.error("Failed to fetch story seeds:", e);
    } finally {
      setLoadingSeeds(false);
    }
  };

  // Auto-fill pet info when pet is selected
  useEffect(() => {
    if (formData.pet_id && pets.length > 0) {
      const pet = pets.find(p => p.id === formData.pet_id);
      if (pet) {
        const traits = [
          pet.breed,
          pet.activities,
          pet.favorite_toys,
          pet.description
        ].filter(Boolean).join(", ");
        
        setFormData(prev => ({
          ...prev,
          pet_name: pet.name,
          pet_traits: traits.slice(0, 100) // Keep it concise
        }));
      }
    }
  }, [formData.pet_id, pets]);

  // Generate AI story starter
  const generateStarter = async () => {
    if (!formData.pet_name) {
      toast.error("Please enter a pet name first");
      return;
    }
    
    setGeneratingStarter(true);
    try {
      const response = await axios.post(
        `${API}/storybook/ai-starter`,
        {
          pet_name: formData.pet_name,
          pet_traits: formData.pet_traits || null,
          genre: selectedTemplate?.id === "blank" ? (formData.genre || "adventure") : selectedTemplate?.id || "adventure"
        },
        { headers: getAuthHeader() }
      );
      
      if (selectedTemplate?.id === "blank") {
        setFormData(prev => ({ ...prev, full_story: response.data.starter }));
      } else {
        setFormData(prev => ({ ...prev, opening: response.data.starter }));
      }
      toast.success("Story starter created! ✨");
    } catch (e) {
      console.error("Failed to generate starter:", e);
      toast.error("Couldn't generate starter. Try again!");
    } finally {
      setGeneratingStarter(false);
    }
  };

  // Save story
  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Please give your story a title");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        template: selectedTemplate?.id || "blank",
        obstacles: formData.obstacles.filter(o => o.trim())
      };
      
      if (editingEntry) {
        await axios.put(`${API}/storybook/${editingEntry.id}`, payload, { headers: getAuthHeader() });
        toast.success("Story updated! 📖");
      } else {
        await axios.post(`${API}/storybook`, payload, { headers: getAuthHeader() });
        toast.success("Story saved! 📖");
      }
      
      await fetchEntries();
      resetForm();
    } catch (e) {
      console.error("Failed to save story:", e);
      toast.error("Couldn't save story");
    } finally {
      setLoading(false);
    }
  };

  // Delete story
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    
    try {
      await axios.delete(`${API}/storybook/${id}`, { headers: getAuthHeader() });
      toast.success("Story deleted");
      await fetchEntries();
      if (viewingEntry?.id === id) setViewingEntry(null);
    } catch (e) {
      toast.error("Couldn't delete story");
    }
  };

  // Edit story
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setSelectedTemplate(TEMPLATES.find(t => t.id === entry.template) || TEMPLATES[6]);
    setFormData({
      title: entry.title || "",
      genre: entry.genre || "",
      mood: entry.mood || "",
      pet_id: entry.pet_id || "",
      pet_name: entry.pet_name || "",
      pet_traits: entry.pet_traits || "",
      opening: entry.opening || "",
      goal: entry.goal || "",
      obstacles: entry.obstacles?.length ? entry.obstacles : ["", ""],
      climax: entry.climax || "",
      ending: entry.ending || "",
      full_story: entry.full_story || "",
      illustration: entry.illustration || "",
      paired_song_id: entry.paired_song_id || "",
      memory_note: entry.memory_note || "",
      // Through Their Eyes fields
      their_view: entry.their_view || "",
      favorite_moment: entry.favorite_moment || "",
      dreams: entry.dreams || "",
      treasure: entry.treasure || "",
      wish_you_knew: entry.wish_you_knew || "",
      // Letter to My Pet fields
      letter_opening: entry.letter_opening || "",
      letter_gratitude: entry.letter_gratitude || "",
      letter_memory: entry.letter_memory || "",
      letter_promise: entry.letter_promise || "",
      letter_closing: entry.letter_closing || "",
      // Rainbow Bridge fields
      rainbow_intro: entry.rainbow_intro || "",
      rainbow_joy: entry.rainbow_joy || "",
      rainbow_lesson: entry.rainbow_lesson || "",
      rainbow_missing: entry.rainbow_missing || "",
      rainbow_love: entry.rainbow_love || ""
    });
    setShowCreator(true);
    setViewingEntry(null);
  };

  // Generate PDF mini-book
  const generatePdf = async (entry) => {
    setGeneratingPdf(true);
    toast.info("Creating your mini-book... 📖");
    
    try {
      const template = TEMPLATES.find(t => t.id === entry.template);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5' // Smaller, book-like format
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      
      // Helper to add wrapped text
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, i) => {
          pdf.text(line, x, y + (i * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };
      
      // === COVER PAGE ===
      // Background color (soft cream)
      pdf.setFillColor(255, 250, 240);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Decorative border
      pdf.setDrawColor(139, 115, 85); // Warm brown
      pdf.setLineWidth(1);
      pdf.rect(8, 8, pageWidth - 16, pageHeight - 16);
      
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.setTextColor(60, 60, 60);
      const titleLines = pdf.splitTextToSize(entry.title, contentWidth);
      const titleY = entry.illustration ? 120 : 80;
      titleLines.forEach((line, i) => {
        pdf.text(line, pageWidth / 2, titleY + (i * 12), { align: 'center' });
      });
      
      // Pet name
      if (entry.pet_name) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(14);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Starring: ${entry.pet_name}`, pageWidth / 2, titleY + (titleLines.length * 12) + 15, { align: 'center' });
      }
      
      // Template badge
      if (template) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`A ${template.name}`, pageWidth / 2, pageHeight - 25, { align: 'center' });
      }
      
      // Illustration on cover (if exists)
      if (entry.illustration) {
        try {
          const imgWidth = 60;
          const imgHeight = 60;
          const imgX = (pageWidth - imgWidth) / 2;
          pdf.addImage(entry.illustration, 'JPEG', imgX, 35, imgWidth, imgHeight);
        } catch (e) {
          console.log("Could not add illustration to PDF");
        }
      }
      
      // Paw print decoration
      pdf.setFontSize(20);
      pdf.text('🐾', pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // === STORY PAGES ===
      pdf.addPage();
      pdf.setFillColor(255, 253, 250);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      let currentY = margin + 10;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(50, 50, 50);
      
      if (entry.template === "blank") {
        // Full story text
        if (entry.full_story) {
          currentY = addWrappedText(entry.full_story, margin, currentY, contentWidth, 6);
        }
      } else if (entry.template === "through-their-eyes") {
        // Through Their Eyes - First person pet perspective
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.setTextColor(100, 149, 237); // Cornflower blue
        const introText = `The world through ${entry.pet_name || "my"}'s eyes...`;
        pdf.text(introText, pageWidth / 2, currentY, { align: 'center' });
        currentY += 12;
        
        pdf.setTextColor(50, 50, 50);
        
        if (entry.their_view) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("When I Look at You...", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.their_view, margin, currentY, contentWidth);
          currentY += 10;
        }
        
        if (entry.favorite_moment) {
          if (currentY > pageHeight - 50) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("My Favorite Moment", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.favorite_moment, margin, currentY, contentWidth);
          currentY += 10;
        }
        
        if (entry.dreams) {
          if (currentY > pageHeight - 50) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("What I Dream About", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.dreams, margin, currentY, contentWidth);
          currentY += 10;
        }
        
        if (entry.treasure) {
          if (currentY > pageHeight - 50) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("A Moment I Treasure", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.treasure, margin, currentY, contentWidth);
          currentY += 10;
        }
        
        if (entry.wish_you_knew) {
          if (currentY > pageHeight - 50) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("What I Wish You Knew", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.wish_you_knew, margin, currentY, contentWidth);
          currentY += 10;
        }
      } else {
        // Structured story (adventure, mystery, cozy)
        if (entry.opening) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("✨ The Beginning", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.opening, margin, currentY, contentWidth);
          currentY += 8;
        }
        
        if (entry.goal) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("🎯 The Quest", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.goal, margin, currentY, contentWidth);
          currentY += 8;
        }
        
        // Check if we need a new page
        if (currentY > pageHeight - 50) {
          pdf.addPage();
          pdf.setFillColor(255, 253, 250);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          currentY = margin + 10;
        }
        
        if (entry.obstacles?.filter(o => o).length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("⚡ The Challenges", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          entry.obstacles.filter(o => o).forEach((obs, i) => {
            currentY = addWrappedText(`${i + 1}. ${obs}`, margin, currentY, contentWidth);
            currentY += 3;
          });
          currentY += 5;
        }
        
        if (entry.climax) {
          if (currentY > pageHeight - 40) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("🌟 The Big Moment", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.climax, margin, currentY, contentWidth);
          currentY += 8;
        }
        
        if (entry.ending) {
          if (currentY > pageHeight - 40) {
            pdf.addPage();
            pdf.setFillColor(255, 253, 250);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            currentY = margin + 10;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.text("🏁 The Ending", margin, currentY);
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          currentY = addWrappedText(entry.ending, margin, currentY, contentWidth);
        }
      }
      
      // === BACK PAGE with memory note ===
      if (entry.memory_note) {
        pdf.addPage();
        pdf.setFillColor(255, 250, 240);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(12);
        pdf.setTextColor(100, 80, 60);
        pdf.text("💭 What made me smile...", pageWidth / 2, 60, { align: 'center' });
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        const noteLines = pdf.splitTextToSize(entry.memory_note, contentWidth - 20);
        noteLines.forEach((line, i) => {
          pdf.text(line, pageWidth / 2, 75 + (i * 7), { align: 'center' });
        });
        
        // Footer
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text("Made with love at Paws & Harmony 🐾", pageWidth / 2, pageHeight - 20, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(), pageWidth / 2, pageHeight - 14, { align: 'center' });
      }
      
      // Save the PDF
      const fileName = `${entry.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_storybook.pdf`;
      pdf.save(fileName);
      
      toast.success("Mini-book downloaded! 📖✨");
    } catch (e) {
      console.error("PDF generation error:", e);
      toast.error("Couldn't create PDF. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setShowCreator(false);
    setSelectedTemplate(null);
    setEditingEntry(null);
    setStorySeeds([]);
    setFormData({
      title: "",
      genre: "",
      mood: "",
      pet_id: "",
      pet_name: "",
      pet_traits: "",
      opening: "",
      goal: "",
      obstacles: ["", ""],
      climax: "",
      ending: "",
      full_story: "",
      illustration: "",
      paired_song_id: "",
      memory_note: "",
      // Through Their Eyes fields
      their_view: "",
      favorite_moment: "",
      dreams: "",
      treasure: "",
      wish_you_knew: "",
      // Letter to My Pet fields
      letter_opening: "",
      letter_gratitude: "",
      letter_memory: "",
      letter_promise: "",
      letter_closing: "",
      // Rainbow Bridge fields
      rainbow_intro: "",
      rainbow_joy: "",
      rainbow_lesson: "",
      rainbow_missing: "",
      rainbow_love: ""
    });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image too large (max 5MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, illustration: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Render story viewer
  if (viewingEntry) {
    const template = TEMPLATES.find(t => t.id === viewingEntry.template);
    const pairedSong = songs.find(s => s.id === viewingEntry.paired_song_id);
    
    return (
      <div data-testid="storybook-viewer" className="fade-in">
        <button 
          onClick={() => setViewingEntry(null)}
          className="flex items-center gap-2 text-sage hover:text-sage-dark mb-6"
        >
          <ChevronLeft size={20} /> Back to Stories
        </button>
        
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${template?.color || 'from-sage to-teal-500'} p-8 text-white`}>
            <div className="flex items-center gap-3 mb-4">
              {template?.icon && <template.icon size={32} />}
              <span className="text-white/80">{template?.name || 'Story'}</span>
            </div>
            <h1 className="font-heading text-4xl font-bold">{viewingEntry.title}</h1>
            {viewingEntry.pet_name && (
              <p className="text-white/90 mt-2 flex items-center gap-2">
                <PawPrint size={18} /> Starring: {viewingEntry.pet_name}
              </p>
            )}
          </div>
          
          {/* Illustration */}
          {viewingEntry.illustration && (
            <div className="p-6 border-b border-gray-100">
              <img 
                src={viewingEntry.illustration} 
                alt="Story illustration" 
                className="max-w-md mx-auto rounded-2xl shadow-lg"
              />
            </div>
          )}
          
          {/* Story Content */}
          <div className="p-8">
            {viewingEntry.template === "blank" ? (
              <div className="prose max-w-none">
                <p className="text-lg text-charcoal whitespace-pre-wrap leading-relaxed">
                  {viewingEntry.full_story}
                </p>
              </div>
            ) : viewingEntry.template === "through-their-eyes" ? (
              <div className="space-y-6">
                <div className="p-4 bg-sky-50 rounded-xl border border-sky-200 mb-4 text-center">
                  <p className="text-sky-700 italic">
                    The world through {viewingEntry.pet_name || "my"}'s eyes... 👀
                  </p>
                </div>
                {viewingEntry.their_view && (
                  <div>
                    <h3 className="font-medium text-sky-600 mb-2">👀 When I Look at You...</h3>
                    <p className="text-charcoal italic">{viewingEntry.their_view}</p>
                  </div>
                )}
                {viewingEntry.favorite_moment && (
                  <div>
                    <h3 className="font-medium text-sky-600 mb-2">💕 My Favorite Moment</h3>
                    <p className="text-charcoal italic">{viewingEntry.favorite_moment}</p>
                  </div>
                )}
                {viewingEntry.dreams && (
                  <div>
                    <h3 className="font-medium text-sky-600 mb-2">💭 What I Dream About</h3>
                    <p className="text-charcoal italic">{viewingEntry.dreams}</p>
                  </div>
                )}
                {viewingEntry.treasure && (
                  <div>
                    <h3 className="font-medium text-sky-600 mb-2">🎁 A Moment I Treasure</h3>
                    <p className="text-charcoal italic">{viewingEntry.treasure}</p>
                  </div>
                )}
                {viewingEntry.wish_you_knew && (
                  <div>
                    <h3 className="font-medium text-sky-600 mb-2">🤫 What I Wish You Knew</h3>
                    <p className="text-charcoal italic">{viewingEntry.wish_you_knew}</p>
                  </div>
                )}
              </div>
            ) : viewingEntry.template === "letter-to-pet" ? (
              <div className="space-y-6">
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-200 mb-4 text-center">
                  <p className="text-rose-700 italic">
                    💌 A letter to {viewingEntry.pet_name || "my beloved pet"}...
                  </p>
                </div>
                {viewingEntry.letter_opening && (
                  <div>
                    <h3 className="font-medium text-rose-600 mb-2">💕 Dear {viewingEntry.pet_name || "You"}...</h3>
                    <p className="text-charcoal">{viewingEntry.letter_opening}</p>
                  </div>
                )}
                {viewingEntry.letter_gratitude && (
                  <div>
                    <h3 className="font-medium text-rose-600 mb-2">🙏 I'm grateful for...</h3>
                    <p className="text-charcoal">{viewingEntry.letter_gratitude}</p>
                  </div>
                )}
                {viewingEntry.letter_memory && (
                  <div>
                    <h3 className="font-medium text-rose-600 mb-2">💭 I'll always remember...</h3>
                    <p className="text-charcoal">{viewingEntry.letter_memory}</p>
                  </div>
                )}
                {viewingEntry.letter_promise && (
                  <div>
                    <h3 className="font-medium text-rose-600 mb-2">🤝 I promise to...</h3>
                    <p className="text-charcoal">{viewingEntry.letter_promise}</p>
                  </div>
                )}
                {viewingEntry.letter_closing && (
                  <div>
                    <h3 className="font-medium text-rose-600 mb-2">❤️ With all my love...</h3>
                    <p className="text-charcoal">{viewingEntry.letter_closing}</p>
                  </div>
                )}
              </div>
            ) : viewingEntry.template === "rainbow-bridge" ? (
              <div className="space-y-6">
                <div className="p-4 bg-gradient-to-r from-violet-50 via-pink-50 to-amber-50 rounded-xl border border-violet-200 mb-4 text-center">
                  <p className="text-violet-700 italic">
                    🌈 In loving memory of {viewingEntry.pet_name || "a beloved companion"}...
                  </p>
                </div>
                {viewingEntry.rainbow_intro && (
                  <div>
                    <h3 className="font-medium text-violet-600 mb-2">🌟 Remembering {viewingEntry.pet_name || "You"}...</h3>
                    <p className="text-charcoal">{viewingEntry.rainbow_intro}</p>
                  </div>
                )}
                {viewingEntry.rainbow_joy && (
                  <div>
                    <h3 className="font-medium text-violet-600 mb-2">😊 The joy they brought...</h3>
                    <p className="text-charcoal">{viewingEntry.rainbow_joy}</p>
                  </div>
                )}
                {viewingEntry.rainbow_lesson && (
                  <div>
                    <h3 className="font-medium text-violet-600 mb-2">📚 What they taught me...</h3>
                    <p className="text-charcoal">{viewingEntry.rainbow_lesson}</p>
                  </div>
                )}
                {viewingEntry.rainbow_missing && (
                  <div>
                    <h3 className="font-medium text-violet-600 mb-2">💔 What I miss most...</h3>
                    <p className="text-charcoal">{viewingEntry.rainbow_missing}</p>
                  </div>
                )}
                {viewingEntry.rainbow_love && (
                  <div>
                    <h3 className="font-medium text-violet-600 mb-2">💕 Until we meet again...</h3>
                    <p className="text-charcoal">{viewingEntry.rainbow_love}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {viewingEntry.opening && (
                  <div>
                    <h3 className="font-medium text-sage mb-2">✨ Opening</h3>
                    <p className="text-charcoal">{viewingEntry.opening}</p>
                  </div>
                )}
                {viewingEntry.goal && (
                  <div>
                    <h3 className="font-medium text-sage mb-2">🎯 The Goal</h3>
                    <p className="text-charcoal">{viewingEntry.goal}</p>
                  </div>
                )}
                {viewingEntry.obstacles?.filter(o => o).length > 0 && (
                  <div>
                    <h3 className="font-medium text-sage mb-2">⚡ Challenges</h3>
                    <ul className="list-disc list-inside text-charcoal space-y-1">
                      {viewingEntry.obstacles.filter(o => o).map((obs, i) => (
                        <li key={i}>{obs}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewingEntry.climax && (
                  <div>
                    <h3 className="font-medium text-sage mb-2">🌟 Big Moment</h3>
                    <p className="text-charcoal">{viewingEntry.climax}</p>
                  </div>
                )}
                {viewingEntry.ending && (
                  <div>
                    <h3 className="font-medium text-sage mb-2">🏁 Ending</h3>
                    <p className="text-charcoal">{viewingEntry.ending}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Memory Note */}
            {viewingEntry.memory_note && (
              <div className="mt-8 p-4 bg-amber-50 rounded-2xl border border-amber-200">
                <h3 className="font-medium text-amber-700 mb-2">💭 What made you smile?</h3>
                <p className="text-amber-800">{viewingEntry.memory_note}</p>
              </div>
            )}
            
            {/* Paired Song */}
            {pairedSong && (
              <div className="mt-6 p-4 bg-sage/10 rounded-2xl flex items-center gap-4">
                <Music className="text-sage" size={24} />
                <div>
                  <p className="text-sm text-stone">Background Music</p>
                  <p className="font-medium text-charcoal">{pairedSong.title}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="p-6 border-t border-gray-100 flex gap-3">
            <button 
              onClick={() => generatePdf(viewingEntry)}
              disabled={generatingPdf}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-full font-medium hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download size={18} /> {generatingPdf ? "Creating..." : "Download PDF"}
            </button>
            <button 
              onClick={() => handleEdit(viewingEntry)}
              className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center justify-center gap-2"
            >
              <Edit2 size={18} /> Edit
            </button>
            <button 
              onClick={() => handleDelete(viewingEntry.id)}
              className="px-6 py-3 bg-red-50 text-red-500 rounded-full hover:bg-red-100 transition-all flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render creator
  if (showCreator) {
    return (
      <div data-testid="storybook-creator" className="fade-in">
        <button 
          onClick={resetForm}
          className="flex items-center gap-2 text-sage hover:text-sage-dark mb-6"
        >
          <ChevronLeft size={20} /> Back
        </button>
        
        {/* Template Selection */}
        {!selectedTemplate ? (
          <div>
            <h2 className="font-heading text-3xl font-bold text-charcoal mb-2">Choose Your Canvas</h2>
            <p className="text-stone mb-8">Pick a template to guide your story, or start with a blank page</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {TEMPLATES.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`p-6 rounded-2xl bg-gradient-to-br ${template.color} text-white text-left hover:scale-[1.02] transition-all shadow-lg`}
                >
                  <template.icon size={32} className="mb-3" />
                  <h3 className="font-heading text-xl font-bold">{template.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{template.description}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-8">
            {/* Template Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${selectedTemplate.color}`}>
                <selectedTemplate.icon size={24} className="text-white" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-charcoal">{selectedTemplate.name}</h2>
                <p className="text-stone text-sm">{selectedTemplate.description}</p>
              </div>
            </div>
            
            {/* Title */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-charcoal mb-2">Story Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Give your story a name..."
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
              />
            </div>
            
            {/* Pet Selection */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  <PawPrint size={16} className="inline mr-1" /> Pet (optional)
                </label>
                {pets.length > 0 ? (
                  <select
                    value={formData.pet_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, pet_id: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
                  >
                    <option value="">Choose a pet...</option>
                    {pets.map(pet => (
                      <option key={pet.id} value={pet.id}>{pet.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.pet_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, pet_name: e.target.value }))}
                    placeholder="Enter pet name..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Pet Traits (for AI)</label>
                <input
                  type="text"
                  value={formData.pet_traits}
                  onChange={(e) => setFormData(prev => ({ ...prev, pet_traits: e.target.value }))}
                  placeholder="e.g., curious, loves tunnels, always hungry"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
                />
              </div>
            </div>
            
            {/* AI Story Starter Button */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-purple-800 flex items-center gap-2">
                    <Wand2 size={18} /> AI Story Starter
                  </h3>
                  <p className="text-sm text-purple-600">Let AI spark your creativity with an opening line</p>
                </div>
                <button
                  onClick={generateStarter}
                  disabled={generatingStarter || !formData.pet_name}
                  className="px-4 py-2 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {generatingStarter ? (
                    <>
                      <span className="animate-spin">✨</span> Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} /> Give me an idea!
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Story Seeds - Inspiration from your memories */}
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium text-amber-800 flex items-center gap-2">
                    <Lightbulb size={18} /> Story Seeds
                  </h3>
                  <p className="text-sm text-amber-600">Get inspiration from your memories and journal</p>
                </div>
                <button
                  onClick={fetchStorySeeds}
                  disabled={loadingSeeds}
                  className="px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {loadingSeeds ? (
                    <>
                      <span className="animate-spin">🌱</span> Finding...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} /> Find inspiration
                    </>
                  )}
                </button>
              </div>
              {storySeeds.length > 0 && (
                <div className="space-y-2 mt-3 pt-3 border-t border-amber-200">
                  {storySeeds.map((seed, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        // Use the seed as inspiration in the opening/first field
                        if (selectedTemplate.id === "blank") {
                          setFormData(prev => ({ ...prev, full_story: `Inspired by this memory: "${seed.text}"\n\n` }));
                        } else if (selectedTemplate.id === "through-their-eyes") {
                          setFormData(prev => ({ ...prev, their_view: seed.text }));
                        } else if (selectedTemplate.id === "letter-to-pet") {
                          setFormData(prev => ({ ...prev, letter_memory: seed.text }));
                        } else if (selectedTemplate.id === "rainbow-bridge") {
                          setFormData(prev => ({ ...prev, rainbow_joy: seed.text }));
                        } else {
                          setFormData(prev => ({ ...prev, opening: `Inspired by: "${seed.text}"\n\n` }));
                        }
                        toast.success("Memory added as inspiration! ✨");
                      }}
                      className="w-full text-left p-3 bg-white rounded-xl hover:bg-amber-100 transition-all border border-amber-100"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{seed.emoji}</span>
                        <div className="flex-1">
                          <p className="text-sm text-charcoal">{seed.text}</p>
                          {seed.pet_name && <p className="text-xs text-amber-600 mt-1">About: {seed.pet_name}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Story Content - Blank Template */}
            {selectedTemplate.id === "blank" ? (
              <div className="mb-6">
                <label className="block text-sm font-medium text-charcoal mb-2">Your Story</label>
                <textarea
                  value={formData.full_story}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_story: e.target.value }))}
                  placeholder="Once upon a time..."
                  rows={12}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none resize-none"
                />
              </div>
            ) : selectedTemplate.id === "through-their-eyes" ? (
              /* Story Content - Through Their Eyes Template */
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-sky-50 rounded-xl border border-sky-200 mb-4">
                  <p className="text-sky-700 text-sm italic">
                    ✨ Write as if you ARE {formData.pet_name || "your pet"}. Use "I" and imagine seeing the world through their eyes...
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">👀 When I Look at You...</label>
                  <textarea
                    value={formData.their_view}
                    onChange={(e) => setFormData(prev => ({ ...prev, their_view: e.target.value }))}
                    placeholder="What do I see when I look at my human? What do I notice about them that makes me happy?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💕 My Favorite Moment of the Day</label>
                  <textarea
                    value={formData.favorite_moment}
                    onChange={(e) => setFormData(prev => ({ ...prev, favorite_moment: e.target.value }))}
                    placeholder="The best part of my day is when... I love it because..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💭 What I Dream About</label>
                  <textarea
                    value={formData.dreams}
                    onChange={(e) => setFormData(prev => ({ ...prev, dreams: e.target.value }))}
                    placeholder="When I close my eyes and drift off to sleep, I dream of..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🎁 A Moment I Treasure</label>
                  <textarea
                    value={formData.treasure}
                    onChange={(e) => setFormData(prev => ({ ...prev, treasure: e.target.value }))}
                    placeholder="There's one memory I hold close to my heart... It was the time when..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🤫 What I Wish You Knew</label>
                  <textarea
                    value={formData.wish_you_knew}
                    onChange={(e) => setFormData(prev => ({ ...prev, wish_you_knew: e.target.value }))}
                    placeholder="If I could tell you one thing, it would be..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-400 outline-none resize-none"
                  />
                </div>
              </div>
            ) : selectedTemplate.id === "letter-to-pet" ? (
              /* Story Content - Letter to My Pet Template */
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-200 mb-4">
                  <p className="text-rose-700 text-sm italic">
                    💌 Write a heartfelt letter to {formData.pet_name || "your pet"}. Let your love flow onto the page...
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💕 Dear {formData.pet_name || "[Pet Name]"}...</label>
                  <textarea
                    value={formData.letter_opening}
                    onChange={(e) => setFormData(prev => ({ ...prev, letter_opening: e.target.value }))}
                    placeholder="How do you want to start your letter? What do you want them to know first?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-rose-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🙏 I'm grateful for...</label>
                  <textarea
                    value={formData.letter_gratitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, letter_gratitude: e.target.value }))}
                    placeholder="What are you thankful for? How have they enriched your life?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-rose-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💭 I'll always remember...</label>
                  <textarea
                    value={formData.letter_memory}
                    onChange={(e) => setFormData(prev => ({ ...prev, letter_memory: e.target.value }))}
                    placeholder="Share a special memory that you treasure..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-rose-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🤝 I promise to...</label>
                  <textarea
                    value={formData.letter_promise}
                    onChange={(e) => setFormData(prev => ({ ...prev, letter_promise: e.target.value }))}
                    placeholder="What promises do you want to make? What commitment do you want to express?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-rose-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">❤️ With all my love...</label>
                  <textarea
                    value={formData.letter_closing}
                    onChange={(e) => setFormData(prev => ({ ...prev, letter_closing: e.target.value }))}
                    placeholder="How do you want to close your letter? Final words of love..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-rose-400 outline-none resize-none"
                  />
                </div>
              </div>
            ) : selectedTemplate.id === "rainbow-bridge" ? (
              /* Story Content - Rainbow Bridge Memorial Template */
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-gradient-to-r from-violet-50 via-pink-50 to-amber-50 rounded-xl border border-violet-200 mb-4">
                  <p className="text-violet-700 text-sm italic">
                    🌈 A loving tribute to {formData.pet_name || "your beloved companion"} who now runs free across the Rainbow Bridge...
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🌟 Remembering {formData.pet_name || "[Pet Name]"}...</label>
                  <textarea
                    value={formData.rainbow_intro}
                    onChange={(e) => setFormData(prev => ({ ...prev, rainbow_intro: e.target.value }))}
                    placeholder="Introduce your beloved pet. Who were they? What made them special?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">😊 The joy they brought...</label>
                  <textarea
                    value={formData.rainbow_joy}
                    onChange={(e) => setFormData(prev => ({ ...prev, rainbow_joy: e.target.value }))}
                    placeholder="What happiness did they bring to your life? What made you smile?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">📚 What they taught me...</label>
                  <textarea
                    value={formData.rainbow_lesson}
                    onChange={(e) => setFormData(prev => ({ ...prev, rainbow_lesson: e.target.value }))}
                    placeholder="What lessons did they teach you about life, love, or yourself?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💔 What I miss most...</label>
                  <textarea
                    value={formData.rainbow_missing}
                    onChange={(e) => setFormData(prev => ({ ...prev, rainbow_missing: e.target.value }))}
                    placeholder="What do you miss about them? The little things, the big things..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-400 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">💕 Until we meet again...</label>
                  <textarea
                    value={formData.rainbow_love}
                    onChange={(e) => setFormData(prev => ({ ...prev, rainbow_love: e.target.value }))}
                    placeholder="Your final words of love. What do you want them to know?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-violet-400 outline-none resize-none"
                  />
                </div>
              </div>
            ) : (
              /* Story Content - Guided Templates */
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">✨ Opening Hook</label>
                  <textarea
                    value={formData.opening}
                    onChange={(e) => setFormData(prev => ({ ...prev, opening: e.target.value }))}
                    placeholder="Start your story..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🎯 The Goal</label>
                  <input
                    type="text"
                    value={formData.goal}
                    onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value }))}
                    placeholder="What does our hero want to achieve?"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">⚡ Challenges (up to 2)</label>
                  {formData.obstacles.map((obs, i) => (
                    <input
                      key={i}
                      type="text"
                      value={obs}
                      onChange={(e) => {
                        const newObs = [...formData.obstacles];
                        newObs[i] = e.target.value;
                        setFormData(prev => ({ ...prev, obstacles: newObs }));
                      }}
                      placeholder={`Challenge ${i + 1}...`}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none mb-2"
                    />
                  ))}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🌟 The Big Moment</label>
                  <textarea
                    value={formData.climax}
                    onChange={(e) => setFormData(prev => ({ ...prev, climax: e.target.value }))}
                    placeholder="The exciting turning point..."
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">🏁 The Ending</label>
                  <textarea
                    value={formData.ending}
                    onChange={(e) => setFormData(prev => ({ ...prev, ending: e.target.value }))}
                    placeholder="How does it all wrap up?"
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none resize-none"
                  />
                </div>
              </div>
            )}
            
            {/* Illustration */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-charcoal mb-2">
                <Image size={16} className="inline mr-1" /> Add an Illustration (optional)
              </label>
              {formData.illustration ? (
                <div className="relative inline-block">
                  <img src={formData.illustration} alt="Story illustration" className="max-w-xs rounded-xl" />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, illustration: "" }))}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-sage transition-all">
                  <div className="text-center">
                    <Image size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Click to upload</p>
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              )}
            </div>
            
            {/* Song Pairing */}
            {songs.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-charcoal mb-2">
                  <Music size={16} className="inline mr-1" /> Pair with a Song (optional)
                </label>
                <select
                  value={formData.paired_song_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, paired_song_id: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
                >
                  <option value="">Choose a song...</option>
                  {songs.map(song => (
                    <option key={song.id} value={song.id}>{song.title}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Memory Note */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-charcoal mb-2">💭 What made you smile while writing this?</label>
              <input
                type="text"
                value={formData.memory_note}
                onChange={(e) => setFormData(prev => ({ ...prev, memory_note: e.target.value }))}
                placeholder="A little memory to treasure..."
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sage outline-none"
              />
            </div>
            
            {/* Save Button */}
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-sage text-white py-4 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save size={20} />
                {loading ? "Saving..." : (editingEntry ? "Update Story" : "Save Story")}
              </button>
              <button
                onClick={resetForm}
                className="px-6 py-4 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render story library
  return (
    <div data-testid="storybook-page" className="fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold text-charcoal mb-2">Storybook Creator 📖</h1>
        <p className="text-stone max-w-xl mx-auto">
          A peaceful escape for creative minds. Write short stories about your beloved pets - 
          adventures, mysteries, or cozy moments. Let your imagination wander.
        </p>
      </div>
      
      {/* Create Button */}
      <div className="flex justify-center mb-8">
        <button
          data-testid="create-story-btn"
          onClick={() => setShowCreator(true)}
          className="bg-gradient-to-r from-sage to-teal-500 text-white px-8 py-4 rounded-full text-lg font-medium hover:shadow-lg transition-all flex items-center gap-3"
        >
          <Plus size={24} /> Create a Story
        </button>
      </div>
      
      {/* Story Library */}
      {entries.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {entries.map(entry => {
            const template = TEMPLATES.find(t => t.id === entry.template);
            return (
              <button
                key={entry.id}
                onClick={() => setViewingEntry(entry)}
                className="text-left bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-1"
              >
                {entry.illustration ? (
                  <div className="h-32 overflow-hidden">
                    <img src={entry.illustration} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className={`h-32 bg-gradient-to-br ${template?.color || 'from-sage to-teal-500'} flex items-center justify-center`}>
                    {template?.icon && <template.icon size={48} className="text-white/50" />}
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">
                      {template?.name || 'Story'}
                    </span>
                    {entry.pet_name && (
                      <span className="text-xs text-sage flex items-center gap-1">
                        <PawPrint size={12} /> {entry.pet_name}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-charcoal line-clamp-1">{entry.title}</h3>
                  <p className="text-sm text-stone mt-1">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white/80 rounded-3xl">
          <BookOpen size={48} className="mx-auto text-sage/50 mb-4" />
          <h3 className="font-heading text-xl text-charcoal mb-2">Your storybook is empty</h3>
          <p className="text-stone">Create your first story and let your creativity flow! ✨</p>
        </div>
      )}
    </div>
  );
};

export default StorybookCreatorPage;
